import {
  QUERY_PROGRESS_SUCCEED,
  RECARE_FILE_TYPE,
  SEARCH_ACTION_DELETE_FORM,
} from "core/consts";
import { getErrorMessage } from "core/model/utils/errors";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { Auction, QueryProgress, TransitionalCareFormData } from "core/types";
import ConfirmationDialog from "ds_legacy/components/ConfirmationDialog";
import { useConfirmationDialog } from "ds_legacy/components/ConfirmationDialog/useConfirmationDialog";
import RSButton from "ds_legacy/components/RSButton";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { margin } from "ds_legacy/materials/metrics";
import { useDeleteUnsharedFileByType } from "dsl/ecosystems/PatientFiles/useDeleteUnsharedFileByType";
import { FormSectionsWrapper } from "dsl/ecosystems/PatientForms/components";
import { useImportFormData } from "dsl/ecosystems/PatientForms/utils";
import useSearchAction from "dsl/hooks/useSearchAction";
import { PRODUCT_TOURS, tourAttributes } from "dsl/molecules/useProductTour";
import { useTransitionalCareTour } from "dsl/molecules/useProductTour/tours/TransitionalCareTour";
import { FormWatcher } from "react-forms-state";
import { useTranslations } from "translations";
import { getFormGetters } from "../Import";
import { SectionA } from "./SectionA";
import { SectionAHeader } from "./SectionA/header";
import { SectionB } from "./SectionB";
import { SectionC } from "./SectionC";
import { SectionD } from "./SectionD";
import { SectionE } from "./SectionE";
import { SectionF } from "./SectionF";
import { SectionHeader } from "./SectionHeader";
import { SectionOne } from "./SectionOne";
import { SectionTwo } from "./SectionTwo";

const useDeleteConfirmation = ({
  auction,
  formOnChange,
}: {
  auction: Auction;
  formOnChange: (value: any, statePath: string, validation: any) => void;
}) => {
  const translations = useTranslations();

  const [deleteForm, deleteFormProgress] = useSearchAction({
    actionType: SEARCH_ACTION_DELETE_FORM,
  });
  const [confirmProps, confirm] = useConfirmationDialog();
  const { deleteUnsharedFile } = useDeleteUnsharedFileByType({
    auctionId: auction.id,
    recareFileType: RECARE_FILE_TYPE.TRANSITIONAL_CARE,
  });

  const onDelete = async (formValue: TransitionalCareFormData) => {
    try {
      const confirmed = await confirm({
        cancelText: translations.patientForms.fileDeletion.cancelButton,
        confirmButtonProps: {
          color: "error",
          variant: "contained",
        },
        content: translations.patientForms.fileDeletion.text,
        submitText: translations.patientForms.fileDeletion.confirmButton,
        title: translations.patientForms.fileDeletion.title,
      });

      if (!confirmed) return null;

      await deleteForm({
        auction,
        context: {
          form_type: RECARE_FILE_TYPE.TRANSITIONAL_CARE,
        },
      });

      await deleteUnsharedFile();

      Object.keys(formValue).forEach((key) => formOnChange(null, key, null));
    } catch (err) {
      console.error(
        `error deleting transitional care form: ${getErrorMessage(err)}`,
      );
    }
  };

  return { confirmProps, deleteFormProgress, onDelete };
};

const FormButtons = ({
  auction,
  formOnChange,
  hasPreviousFormValue,
  onSubmitProgress,
  submit,
  withDelete,
}: {
  auction: Auction;
  formOnChange: (value: any, statePath: string, validation: any) => void;
  hasPreviousFormValue: boolean;
  onSubmitProgress: QueryProgress;
  submit: () => void;
  withDelete?: boolean;
}) => {
  useTransitionalCareTour({ auction, hasPreviousFormValue });

  const translations = useTranslations();
  const getOntology = useGetOntology();

  const { importFormData, importProgress } = useImportFormData({
    auction,
    formGetters: getFormGetters(getOntology, translations),
    formOnChange,
  });
  const { confirmProps, deleteFormProgress, onDelete } = useDeleteConfirmation({
    auction,
    formOnChange,
  });

  const isFormFilled =
    hasPreviousFormValue || importProgress === QUERY_PROGRESS_SUCCEED;

  return (
    <>
      <HorizontalLayout
        justify={withDelete ? "space-between" : "flex-end"}
        margin={margin(0, 0, 1)}
      >
        {withDelete && (
          <FormWatcher watchPath="">
            {({ watchedValue }) => {
              return (
                <RSButton
                  color="error"
                  id="delete-form"
                  loading={deleteFormProgress}
                  type="submit"
                  variant="outlined"
                  onClick={() =>
                    onDelete(watchedValue as TransitionalCareFormData)
                  }
                >
                  {translations.patientForms.transitionalCareForm.deleteForm}
                </RSButton>
              );
            }}
          </FormWatcher>
        )}
        <HorizontalLayout justify="flex-end">
          <div
            {...tourAttributes({
              stepKey:
                PRODUCT_TOURS.transitional_care.steps.SAVE_CREATE_PDF.key,
              tourKey: PRODUCT_TOURS.transitional_care.key,
            })}
          >
            <RSButton
              color="primary"
              id={hasPreviousFormValue ? "overwrite-pdf" : "generate-pdf"}
              loading={onSubmitProgress}
              type="submit"
              variant="outlined"
              onClick={submit}
            >
              {hasPreviousFormValue
                ? translations.patientForms.buttonSaveOverwrite
                : translations.patientForms.buttonSave}
            </RSButton>
          </div>
          <div
            {...tourAttributes({
              stepKey: PRODUCT_TOURS.transitional_care.steps.IMPORT_DATA.key,
              tourKey: PRODUCT_TOURS.transitional_care.key,
            })}
          >
            <RSButton
              color="primary"
              id="import-auction"
              loading={importProgress}
              type="submit"
              variant={isFormFilled ? "outlined" : "contained"}
              onClick={importFormData}
            >
              {isFormFilled
                ? translations.patientForms.buttonRefreshData
                : translations.patientForms.buttonImport}
            </RSButton>
          </div>
        </HorizontalLayout>
      </HorizontalLayout>
      <ConfirmationDialog {...confirmProps} />
    </>
  );
};

export function TransitionalCareFormPresenter({
  auction,
  formOnChange,
  hasPreviousFormValue,
  onSubmitProgress,
  submit,
}: {
  auction: Auction;
  formOnChange: (value: any, statePath: string, validation: any) => void;
  hasPreviousFormValue: boolean;
  onSubmitProgress: QueryProgress;
  submit: () => void;
}) {
  const ButtonSection = ({ withDelete }: { withDelete?: boolean }) => (
    <FormButtons
      auction={auction}
      formOnChange={formOnChange}
      hasPreviousFormValue={hasPreviousFormValue}
      submit={submit}
      withDelete={withDelete}
      onSubmitProgress={onSubmitProgress}
    />
  );

  return (
    <VerticalLayout margin={margin(3, 0)} width="100%">
      <SectionHeader>
        <ButtonSection />
      </SectionHeader>
      <FormSectionsWrapper>
        <SectionOne />
        <SectionTwo />
      </FormSectionsWrapper>
      <SectionAHeader />
      <FormSectionsWrapper hideBottomMargin>
        <SectionA />
        <SectionB />
        <SectionC />
        <SectionD />
        <SectionE auctionId={auction.id} />
        <SectionF />
      </FormSectionsWrapper>
      <ButtonSection withDelete />
    </VerticalLayout>
  );
}
